import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { WppSideModal, WppTypography } from 'buildingBlocks';
import { Flight, Member } from 'utils/types';
import { useBulkCreateStrategyWizardContext } from 'containers/BulkCreateStrategyWizard/contexts/BulkCreateStrategyWizardProvider';
import useSideModalHideFreshdesk from 'utils/hooks/useSideModalHideFreshdesk';
import { BULK_CREATE_MAX_FLIGHTS_ALLOWED, BulkUploadTabs, initialFlightsStatus, MEMBER_ERROR, UploadOptions } from 'containers/BulkCreateStrategyWizard/steps/AttachFlights/constants';
import { BULK_CREATE_HELP_CENTER_LINK } from 'containers/App/constants';
import { BulkCreateWizardFormAttachFlights, FlightsStatusType } from 'containers/BulkCreateStrategyWizard/steps/AttachFlights/types';
import { bulkUploaderStyles } from 'containers/BulkCreateStrategyWizard/steps/AttachFlights/style';
import { useBulkCreateAttachModalContext } from 'containers/BulkCreateStrategyWizard/steps/AttachFlights/contexts/BulkCreateAttachModalProvider';
import { checkFlightsEligibility, fetchBulkBudgetAllocationData } from 'containers/BulkCreateStrategyWizard/steps/AttachFlights/utils';
import FlightAttachOptions from './FlightAttachOptions';
import FlightCandidatesValidationProgress from '../AttachFlightsModal/Validation/FlightCandidatesValidationProgress';
import FlightCandidatesAttachedToAnotherStrategy from '../AttachFlightsModal/Validation/FlightCandidatesAttachedToAnotherStrategy';
import BulkUploadModalBody from './BulkUploadModalBody';
import './style.scss';

type Props = {
  bulkUploadModalOpen: boolean
  setBulkUploadModalOpen: (x: boolean) => void
  members: Array<Member>
  memberFetchError: string
};

const { headerStyle, helpCenterStyle } = bulkUploaderStyles;

const BulkUploaderModal = (props: Props) => {
  const { bulkUploadModalOpen, setBulkUploadModalOpen, members, memberFetchError } = props;
  const { setSessionAttachFlights, flightCandidateInfo: { flightCandidates } } = useBulkCreateAttachModalContext();
  const { wizardFormValues, dispatch } = useBulkCreateStrategyWizardContext();
  const { budgetAllocationState } = wizardFormValues;
  const selectedMember = useWatch({ name: 'member' });

  const [selectedTab, setSelectedTab] = useState<string>(BulkUploadTabs.uploadFile);
  const [showFlightAttachOptions, setShowFlightAttachOptions] = useState<boolean>(false);
  const [showValidationProgress, setShowValidationProgress] = useState<boolean>(false);
  const [alreadyAttachedFlightsToInclude, setAlreadyAttachedFlightsToInclude] = useState<Array<Flight>>([]);
  const [flightAttachOption, setFlightAttachOption] = useState<string>(UploadOptions.replaceSelection);
  const [flightsStatus, setFlightsStatus] = useState<FlightsStatusType>(initialFlightsStatus);
  const [bulkFlightIds, setBulkFlightIds] = useState<Array<number | string>>([]);
  const [errMessage, setErrMessage] = useState<string>('');

  const { eligibleFlights, attachedToAnotherStrategy } = flightsStatus;
  const { getValues, setValue } = useFormContext<BulkCreateWizardFormAttachFlights>();
  const optimizationLevel = useWatch({ name: 'optimizationLevel' });
  const flightsAttachedToAnotherStratSelected = !!_.size(attachedToAnotherStrategy);
  const attachedFlights = getValues('attachedFlights');

  const handleModalReset = () => {
    setFlightsStatus(initialFlightsStatus);
    setShowFlightAttachOptions(false);
    setAlreadyAttachedFlightsToInclude([]);
    setShowValidationProgress(false);
    setBulkUploadModalOpen(false);
    setErrMessage('');
    setBulkFlightIds([]);
    setSelectedTab(BulkUploadTabs.uploadFile);
  };

  // hide freshdesk button when side modal is open
  useSideModalHideFreshdesk(bulkUploadModalOpen);

  const handleAttachAndModalReset = (flightsToAttach: Array<Flight>) => {
    setValue('attachedFlights', flightsToAttach);
    fetchBulkBudgetAllocationData(
      flightsToAttach,
      _.get(budgetAllocationState, 'data'),
      dispatch,
      optimizationLevel?.defaultStrat,
    );
    handleModalReset();
  };

  // if flight status changes then eligibility has been checked for the sessionAttachFlights
  useEffect(() => {
    if (!_.isEqual(flightsStatus, initialFlightsStatus)) {
      setShowValidationProgress(false);
      if (!flightsAttachedToAnotherStratSelected) {
        handleAttachAndModalReset(eligibleFlights);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightsStatus]);

  const bulkUpload = async () => {
    setShowFlightAttachOptions(false);
    if (!selectedMember) {
      setErrMessage(MEMBER_ERROR);
      return;
    }
    const flightLimitExceeded = _.size(bulkFlightIds) > BULK_CREATE_MAX_FLIGHTS_ALLOWED;
    if (flightLimitExceeded) {
      setErrMessage(`Maximum object limit ${BULK_CREATE_MAX_FLIGHTS_ALLOWED} exceeded.`);
      return;
    }
    try {
      const attachedFlightIds = _.map(attachedFlights, (flight) => _.toString(flight.externalId));
      const updatedBulkFlightIds = flightAttachOption === UploadOptions.replaceSelection ? bulkFlightIds : _.uniq([...bulkFlightIds, ...attachedFlightIds]);
      const candidates = _.filter(flightCandidates, (flight) => _.includes(updatedBulkFlightIds, _.toString(flight.externalId)));
      if (_.size(candidates)) {
        setErrMessage('');
        if (flightsAttachedToAnotherStratSelected) {
          handleAttachAndModalReset([...eligibleFlights, ...alreadyAttachedFlightsToInclude]);
        } else {
          setShowValidationProgress(true);
          setSessionAttachFlights(candidates);
          await checkFlightsEligibility(candidates, setFlightsStatus);
          setShowValidationProgress(false);
        }
      }
    } catch (error) {
      setErrMessage('Something went wrong. Please try again.');
    }
  };

  const handleSegemntClick = (event) => {
    setSelectedTab(event.detail.value);
    setErrMessage('');
  };

  const handleAttachClick = () => {
    if (!selectedMember) {
      setErrMessage(MEMBER_ERROR);
      return;
    }
    if (_.size(bulkFlightIds) === 0) {
      const errMsg = _.isEqual(selectedTab, BulkUploadTabs.uploadFile) ? 'A SDF file is required.' : 'Object ID(s) are required.';
      setErrMessage(errMsg);
      return;
    }
    const inCorrectIds = _.difference(
      _.map(bulkFlightIds, String),
      _.map(flightCandidates, (flight) => _.toString(flight.externalId)),
    );
    if (_.size(inCorrectIds) > 0) {
      setErrMessage(`Incorrect IDs: ${_.join(inCorrectIds, ',')}`);
      return;
    }
    setErrMessage('');
    if (_.size(attachedFlights) > 0) {
      setShowFlightAttachOptions(true);
    } else {
      bulkUpload();
    }
  };

  const renderModalBody = () => {
    if (showValidationProgress) {
      return <FlightCandidatesValidationProgress />;
    }
    if (flightsAttachedToAnotherStratSelected) {
      return (
        <FlightCandidatesAttachedToAnotherStrategy
          attachedToAnotherStrategy={attachedToAnotherStrategy}
          alreadyAttachedFlightsToInclude={alreadyAttachedFlightsToInclude}
          setAlreadyAttachedFlightsToInclude={setAlreadyAttachedFlightsToInclude}
        />
      );
    }
    if (showFlightAttachOptions) {
      return (
        <FlightAttachOptions
          setFlightAttachOption={setFlightAttachOption}
          flightAttachOption={flightAttachOption}
          bulkFlightIds={bulkFlightIds}
        />
      );
    }
    return (
      <BulkUploadModalBody
        onTabChange={handleSegemntClick}
        selectedTab={selectedTab}
        setBulkFlightIds={setBulkFlightIds}
        errMessage={errMessage}
        members={members}
        memberFetchError={memberFetchError}
        setErrMessage={setErrMessage}
        bulkUploadModalOpen={bulkUploadModalOpen}
        selectedMember={selectedMember}
      />
    );
  };

  const subtitle = `You can ${_.isEqual(selectedTab, BulkUploadTabs.inputId) ? 'manually input object IDs' : 'upload a file'}
  to speed up the workflow. For more details, visit the`;

  return (
    <WppSideModal
      open={bulkUploadModalOpen}
      size="l"
      onWppSideModalClose={handleModalReset}
      onWppSideModalOpenStart={() => setBulkUploadModalOpen(true)}
      disableOutsideClick
      id="bulk-upload-flights-modal"
      actionsConfig={[
        {
          label: 'Cancel',
          variant: 'secondary',
          onClick: handleModalReset,
        },
        {
          label: 'Attach',
          variant: 'primary',
          onClick: showFlightAttachOptions ? bulkUpload : handleAttachClick,
        },
      ]}
    >
      <div slot="header" style={headerStyle}>
        <WppTypography type="2xl-heading" tag="h3">Upload Object IDs</WppTypography>
        {!showFlightAttachOptions && (
          <WppTypography type="s-body">
            {subtitle}
            <a href={BULK_CREATE_HELP_CENTER_LINK} target="_blank" rel="noopener noreferrer" style={helpCenterStyle}> Help Center.</a>
          </WppTypography>
        )}
      </div>
      <div slot="body">
        {renderModalBody()}
      </div>
    </WppSideModal>
  );
};

export default BulkUploaderModal;
